import { useState, useCallback, memo } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Profile } from "@/types/profile";

interface PhotosModalProps {
  profile: Profile;
  isOpen: boolean;
  onClose: () => void;
}

const PhotosModal = memo(({ profile, isOpen, onClose }: PhotosModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % profile.photos.length);
  }, [profile.photos.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + profile.photos.length) % profile.photos.length);
  }, [profile.photos.length]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-scale-in"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-lg mx-4 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Photo container with balloon-themed frame */}
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-pink-200/20 to-sky-200/20 p-2">
          <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-black">
            <img
              src={profile.photos[currentIndex]}
              alt={`Photo ${currentIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Photo counter */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
              {currentIndex + 1} / {profile.photos.length}
            </div>

            {/* Navigation arrows */}
            {profile.photos.length > 1 && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/50 transition-colors"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/50 transition-colors"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>

          {/* Photo dots indicator */}
          <div className="flex justify-center gap-2 mt-3 pb-1">
            {profile.photos.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentIndex 
                    ? "bg-pop w-6" 
                    : "bg-white/50 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});

PhotosModal.displayName = "PhotosModal";

export default PhotosModal;
